import { AsyncMethodReturns, Connection, connectToParent } from 'penpal'
import { eventEmitter, EventTypes } from '~editor/events'
import { useRouter } from 'next/router'
import { GoToForgeEnum } from '~core/global.type'
import { edState } from '~utils/edState'
import { useEffect, useMemo } from 'react'
import { HubData } from '~core/store/hubdata.type'
import useDispatch from '~editor/hooks/useDispatch'

interface PenpalMethods {
  goToForge: (where?: GoToForgeEnum) => Promise<void>
  goToShard: (id?: string | number) => Promise<void>
  goToURL: (url?: string) => Promise<void>
  changeRoute: (value: {
    collectionId: number
    shardId: number
    sourceShardId?: number
  }) => Promise<void>
  rtcSend: (data?: unknown) => Promise<void>
  rtcSendWhenConnected: (data?: unknown) => Promise<void>
  clientMounted: (mounted: boolean) => Promise<void>
  setShardName: (name: string) => Promise<void>
}

let penpal: Connection<PenpalMethods> | undefined

export const usePenpal = (): PenpalMethods => {
  const router = useRouter()
  const dispatch = useDispatch()

  const methods = useMemo(() => {
    if (!penpal) {
      try {
        if (window.self === window.top) {
          console.warn('Not in an iframe, not connecting via penpal')
        } else {
          penpal = connectToParent<PenpalMethods>({
            methods: {
              reload: () => {
                location.reload()
              },
              changeRoute: ({
                token,
                shardId,
                sourceShardId,
              }: {
                token: string
                shardId: string
                sourceShardId?: string
              }) => {
                router?.push?.(
                  `/render?${new URLSearchParams({
                    authToken: token,
                    shardId,
                    ...(sourceShardId ? { sourceShardId } : {}),
                  })}`,
                )
              },
              edState: (newEDState: Record<string, any>) => {
                edState.next({
                  ...edState.value,
                  ...newEDState,
                })
              },
              sendHubData: (data: HubData) => {
                dispatch.hubData.updateData(data)
              },
            },
          })
        }
      } catch (e) {
        // Do nothing
      }
    }

    const wait = async (cb: (c: AsyncMethodReturns<PenpalMethods>) => void) =>
      penpal && cb(await penpal.promise)
    return {
      goToForge: (where?: GoToForgeEnum) =>
        wait((connection) => {
          if (where === GoToForgeEnum.collectionMenu) return
          connection?.goToForge(where)
        }),
      goToShard: (shardId?: number | string) =>
        wait((connection) => {
          connection?.goToShard(shardId)
        }),
      goToURL: (url?: string) =>
        wait((connection) => {
          connection?.goToURL(`https://${url}`)
        }),
      changeRoute: (route: {
        collectionId: number
        shardId: number
        sourceShardId?: number
      }) =>
        wait((connection) => {
          connection?.changeRoute(route)
        }),
      rtcSend: (data?: any) =>
        wait((connection) => {
          connection?.rtcSend(data)
        }),
      rtcSendWhenConnected: (data?: any) =>
        wait((connection) => {
          connection?.rtcSendWhenConnected(data)
        }),
      clientMounted: (isMounted: boolean) =>
        wait((connection) => {
          connection?.clientMounted(isMounted)
        }),
      setShardName: (name: string) =>
        wait((connection) => {
          connection?.setShardName(name)
        }),
    }
  }, [])

  useEffect(() => {
    eventEmitter.on(EventTypes.penpalGoToForge, methods.goToForge)
    eventEmitter.on(EventTypes.penpalGoToShard, methods.goToShard)
    eventEmitter.on(EventTypes.penpalGoToURL, methods.goToURL)
    eventEmitter.on(EventTypes.penpalChangeRoute, methods.changeRoute)
    eventEmitter.on(EventTypes.penpalSetShardName, methods.setShardName)
    eventEmitter.on(EventTypes.rtcSend, methods.rtcSend)
    eventEmitter.on(
      EventTypes.rtcSendWhenConnected,
      methods.rtcSendWhenConnected,
    )

    return () => {
      eventEmitter.off(EventTypes.penpalGoToForge, methods.goToForge)
      eventEmitter.off(EventTypes.penpalGoToShard, methods.goToShard)
      eventEmitter.off(EventTypes.penpalGoToURL, methods.goToURL)
      eventEmitter.off(EventTypes.penpalChangeRoute, methods.changeRoute)
      eventEmitter.off(EventTypes.penpalSetShardName, methods.setShardName)
      eventEmitter.off(EventTypes.rtcSend, methods.rtcSend)
      eventEmitter.off(
        EventTypes.rtcSendWhenConnected,
        methods.rtcSendWhenConnected,
      )
    }
  }, [methods])

  return methods
}
